/**
 * Conditional form groups.
 *
 * Contact Form 7 has no conditional logic of its own, so a group declares what
 * it depends on in markup:
 *
 *   <div class="form-conditional" data-when="maksusitoumus"
 *        data-when-not="Ei voimassaolevaa maksusitoumusta"> … </div>
 *
 *   data-when      the field name the group follows
 *   data-when-is   show when one of these values is chosen (| separated)
 *   data-when-not  show once something is chosen, unless it is one of these
 *
 * Hidden groups have their controls disabled, not merely hidden: a disabled
 * control is not submitted, so CF7 neither validates nor mails a field nobody
 * could see.
 */

const FORM = '.wpcf7-form';
const GROUP = '.form-conditional';

/**
 * The values currently chosen for a field.
 *
 * @param {HTMLFormElement} form Form to read.
 * @param {string}          name Field name, without CF7's array suffix.
 *
 * @return {string[]} Chosen values.
 */
function chosenValues(form, name) {
	const controls = form.querySelectorAll(
		`[name="${name}"], [name="${name}[]"]`
	);

	return Array.from(controls)
		.filter((control) => {
			if (control.type === 'checkbox' || control.type === 'radio') {
				return control.checked;
			}

			return control.value !== '';
		})
		.map((control) => control.value);
}

/**
 * Shows or hides one group.
 *
 * @param {HTMLFormElement} form  The form.
 * @param {HTMLElement}     group The conditional group.
 */
function applyGroup(form, group) {
	const { when, whenIs, whenNot } = group.dataset;
	const values = chosenValues(form, when);

	let visible = false;

	if (whenIs !== undefined) {
		visible = whenIs.split('|').some((value) => values.includes(value));
	} else if (whenNot !== undefined) {
		visible =
			values.length > 0 &&
			!whenNot.split('|').some((value) => values.includes(value));
	}

	// A group inside a hidden group stays hidden, whatever its own answer was.
	const parent = group.parentElement?.closest(GROUP);

	if (parent?.hidden) {
		visible = false;
	}

	group.hidden = !visible;

	group.querySelectorAll('input, select, textarea').forEach((control) => {
		control.disabled = !visible;
	});
}

export function initFormConditionals() {
	document.querySelectorAll(FORM).forEach((form) => {
		const groups = form.querySelectorAll(`${GROUP}[data-when]`);

		if (!groups.length) {
			return;
		}

		// Document order, so a parent is resolved before the groups inside it.
		const update = () => groups.forEach((group) => applyGroup(form, group));

		form.addEventListener('change', update);

		// CF7 empties the fields after a successful send.
		form.addEventListener('wpcf7mailsent', update);

		update();
	});
}
